/**
 * Centralized Token Service
 *
 * Single source of truth for authentication tokens across the entire admin portal.
 * This module is provider-agnostic: it delegates to whichever IAuthProvider is active
 * (Azure, Auth0, Okta, etc.) via a function reference injected at startup.
 *
 * WHY THIS EXISTS:
 *   - Most API service files are plain modules (not React components), so they
 *     can't call useAuth(). They need a module-level function they can import.
 *   - The previous implementation (authTokenService.ts) scraped MSAL's
 *     sessionStorage entries directly, which was fragile, Azure-specific, and
 *     returned ID tokens instead of access tokens.
 *   - This service delegates to IAuthProvider.getAccessToken(), which calls
 *     acquireTokenSilent with the correct API scopes to get a real access token.
 *
 * USAGE:
 *   import { tokenService } from '@/services/tokenService';
 *
 *   // In API interceptors or service methods:
 *   const token = await tokenService.getAccessToken();
 *
 * INITIALIZATION:
 *   AuthContext calls tokenService.initialize(authProvider) once during mount.
 *   Until initialized, getAccessToken() returns null (no crash, just unauthenticated).
 */

import type { IAuthProvider } from '../types/auth.types';
import { readStoredInstitutionContextId } from '../auth/institutionContext';

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

/** Reference to the active auth provider, set during app initialization. */
let _provider: IAuthProvider | null = null;

/** Whether initialize() has been called at least once. */
let _initialized = false;

// ---------------------------------------------------------------------------
// Cached token (avoids hitting acquireTokenSilent on every single API call)
// ---------------------------------------------------------------------------

interface TokenCacheEntry {
  token: string;
  /** Timestamp (ms) when this cache entry was created. */
  cachedAt: number;
}

let _cache: TokenCacheEntry | null = null;

/**
 * How long (ms) to reuse a cached token before calling the provider again.
 * MSAL tokens typically have a 60-90 min lifetime, so 4 minutes is safe and
 * avoids hammering acquireTokenSilent on rapid-fire API calls.
 */
const CACHE_TTL_MS = 4 * 60 * 1000; // 4 minutes

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const tokenService = {
  /**
   * Wire the active auth provider into this service.
   * Called once by AuthContext on mount / when the provider changes.
   *
   * It is safe to call this multiple times (e.g. hot-reload in dev).
   */
  initialize(provider: IAuthProvider): void {
    const providerChanged = _provider !== provider;
    _provider = provider;
    _initialized = true;
    // Preserve cache across re-initialization (e.g. React StrictMode remounts),
    // but clear it when the provider instance actually changes.
    if (providerChanged) {
      _cache = null;
    }
  },

  /**
   * Prime or clear the in-memory access-token cache.
   * Used by auth flows that already received a fresh token (login/callback),
   * so subsequent API calls don't need another silent acquisition.
   */
  setAccessToken(token: string | null): void {
    if (token && token.trim()) {
      _cache = { token, cachedAt: Date.now() };
      return;
    }
    _cache = null;
  },

  /**
   * Get an access token suitable for API calls (Bearer header).
   *
   * Returns null if:
   *   - The service has not been initialized yet (app still booting).
   *   - The user is not authenticated.
   *   - Silent token acquisition fails (e.g. session expired).
   *
   * All API services should treat a null return as "not authenticated"
   * and either skip the Authorization header or redirect to login.
   */
  async getAccessToken(): Promise<string | null> {
    if (!_provider) {
      if (!_initialized) {
        // App is still booting — don't log a warning.
        return null;
      }
      // Provider was set to null (shouldn't happen) — defensive.
      return null;
    }

    // Return cached token if still fresh.
    if (_cache && Date.now() - _cache.cachedAt < CACHE_TTL_MS) {
      return _cache.token;
    }

    try {
      const token = await _provider.getAccessToken();
      if (token) {
        _cache = { token, cachedAt: Date.now() };
      }
      return token;
    } catch {
      // Silent acquisition failed (session expired, network error, etc.)
      _cache = null;
      return null;
    }
  },

  /**
   * Synchronous convenience for code that can't await (e.g. Axios interceptor
   * configs that need a token RIGHT NOW). Returns the last cached token or null.
   *
   * Prefer the async getAccessToken() whenever possible.
   */
  getCachedToken(): string | null {
    if (_cache && Date.now() - _cache.cachedAt < CACHE_TTL_MS) {
      return _cache.token;
    }
    return null;
  },

  /**
   * Get the institution ID from session storage.
   * Respects assumed-role overrides (platform-admin testing feature).
   */
  getInstitutionId(): string | null {
    if (typeof window === 'undefined') return null;
    const assumedInstitutionId = readStoredInstitutionContextId('assumedInstitutionId');
    if (sessionStorage.getItem('assumedRole')) {
      return assumedInstitutionId;
    }

    return readStoredInstitutionContextId('institutionId');
  },

  /**
   * Clear the in-memory token cache.
   * Called on logout or when the user's session changes.
   */
  clearCache(): void {
    _cache = null;
  },

  /**
   * Handle a 401 response from the API.
   * Clears auth data and redirects to /login.
   */
  handleUnauthorized(): void {
    // Clear cache
    _cache = null;

    // Clear session storage auth items
    if (typeof sessionStorage !== 'undefined') {
      const keysToRemove = [
        'isAuthenticated',
        'assumedRole',
        'assumedInstitutionId',
        'assumedInstitutionName',
        'institutionId',
      ];
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));

      // Clear permission caches
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('permissions_')) {
          sessionStorage.removeItem(key);
        }
      });

      // Clear MSAL cache (Azure-specific but harmless for other providers)
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith('msal'))
        .forEach((key) => sessionStorage.removeItem(key));
    }

    // Redirect to login
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  },

  /**
   * Build standard auth headers for an API request.
   * Uses the cached token (sync) — for async-aware callers, prefer
   * getting the token via getAccessToken() and building headers manually.
   */
  createAuthHeaders(
    extra: Record<string, string> = {}
  ): Record<string, string> {
    const token = this.getCachedToken();
    const institutionId = this.getInstitutionId();
    const hasInstitutionHeader =
      Object.prototype.hasOwnProperty.call(extra, 'X-Institution-ID') ||
      Object.prototype.hasOwnProperty.call(extra, 'x-institution-id');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...extra,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (institutionId && !hasInstitutionHeader) {
      headers['X-Institution-ID'] = institutionId;
    }

    return headers;
  },

  /** Whether initialize() has been called. */
  get isInitialized(): boolean {
    return _initialized;
  },
};

// ---------------------------------------------------------------------------
// Convenience re-exports for drop-in replacement of old services
// ---------------------------------------------------------------------------

/**
 * @deprecated Use tokenService.getAccessToken() instead.
 * Kept temporarily so existing imports compile without changes.
 */
export const getAccessToken = (): string | null => tokenService.getCachedToken();

/**
 * @deprecated Use tokenService.getInstitutionId() instead.
 */
export const getInstitutionIdFromSession = (): string | null =>
  tokenService.getInstitutionId();

/**
 * @deprecated Use tokenService.handleUnauthorized() instead.
 */
export const handleUnauthorized = (): void => tokenService.handleUnauthorized();

/**
 * @deprecated Use tokenService.createAuthHeaders() instead.
 */
export const createAuthHeaders = (
  extra: Record<string, string> = {}
): Record<string, string> => tokenService.createAuthHeaders(extra);

/**
 * @deprecated Use tokenService.clearCache() instead.
 */
export const clearTokenCache = (): void => tokenService.clearCache();
